/**
 * Projects API
 */

import { Project, ProjectCreate } from '../types/api';
import { getAPIClient } from './client';
import { getApiBaseUrl } from '../utils/env';

export interface ProjectStats {
  project_id: string;
  project_name: string;
  total_artifacts: number;
  artifacts_by_type: Record<string, number>;
  total_artifact_size_mb: number;
  total_entities: number;
  entities_by_type: Record<string, number>;
  total_chunks: number;
  chunks_by_artifact_type: Record<string, number>;
  total_sources: number;
  sources_by_type: Record<string, number>;
  total_jobs: number;
  completed_jobs: number;
  failed_jobs: number;
  running_jobs: number;
  created_at: string;
  oldest_artifact: string | null;
  newest_artifact: string | null;
}

/**
 * Check if we're running against an on-premise deployment
 * On-premise deployments have issues with query parameters when X-Tenant-ID header is present
 */
function isOnPremiseDeployment(): boolean {
  const deploymentType = import.meta.env.VITE_DEPLOYMENT_TYPE;
  if (deploymentType === 'onprem' || deploymentType === 'client-onprem') {
    return true;
  }
  
  // Also check the API base URL for on-premise patterns
  const apiUrl = getApiBaseUrl();
  const isLocalhost = apiUrl.includes('localhost') || apiUrl.includes('127.0.0.1');
  const isPrivateIP = /(192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[0-1])\.)/.test(apiUrl);
  const isInternalDomain = /\.(local|internal|lan|corp)/.test(apiUrl);
  
  return isLocalhost || isPrivateIP || isInternalDomain;
}

export async function listProjects(tenantId?: string): Promise<Project[]> {
  const client = getAPIClient();
  const isOnPrem = isOnPremiseDeployment();
  
  if (isOnPrem) {
    // WORKAROUND for on-premise: Call /projects without query params to avoid hanging
    // Filter client-side instead. The query param was causing hangs when X-Tenant-ID header is present.
    // This works because /projects returns all projects and we filter by tenant_id client-side.
    const allProjects = await client.get<Project[]>('/projects');
    
    // Filter by tenant_id if provided (client-side filtering)
    if (tenantId) {
      return allProjects.filter(p => p.tenant_id === tenantId);
    }
    
    return allProjects;
  } else {
    // Standard cloud deployment: Use query parameters (works fine)
    const params = tenantId ? { tenant_id: tenantId } : {};
    return client.get<Project[]>('/projects', { params });
  }
}

export async function getProject(projectId: string): Promise<Project> {
  const client = getAPIClient();
  return client.get<Project>(`/projects/${projectId}`);
}

export async function getProjectStats(projectId: string): Promise<ProjectStats> {
  const client = getAPIClient();
  return client.get<ProjectStats>(`/projects/${projectId}/stats`, {
    params: { project_id: projectId }
  });
}

export async function createProject(data: ProjectCreate): Promise<Project> {
  const client = getAPIClient();
  return client.post<Project>('/projects', data);
}

export async function updateProject(projectId: string, data: Partial<ProjectCreate>): Promise<Project> {
  const client = getAPIClient();
  return client.patch<Project>(`/projects/${projectId}`, data);
}

export async function deleteProject(projectId: string): Promise<void> {
  const client = getAPIClient();
  return client.delete(`/projects/${projectId}`);
}


